// PageDescription: 자주 묻는 질문 리스트 페이지
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import FaqTemplate from '@/components/templates/communication/faq/faq-template';
import useSearchMagazines from '@/entities/magazine/hook/useSearchMagazines';
import useFaqViewModel from '@/hooks/faq/useFaqViewModel';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { MagazineType } from '@/types/magazine';
import { addQueryString } from '@/utils/url';

const FaqPage = ({ client }: CustomPageProps) => {
  const viewModel = useFaqViewModel();
  return (
    <>
      <NextSeo
        title='닥터나우 1분 FAQ | 전문 의료진이 자주 묻는 질문에 답변해 드려요.'
        description='비대면 진료, 약 처방, 진료비, 건강 관련 궁금증을 1분 만에 해결해보세요. 닥터나우 FAQ로 쉽고 빠르게 확인할 수 있어요.'
        openGraph={{
          title: '닥터나우 1분 FAQ | 전문 의료진이 자주 묻는 질문에 답변해 드려요.',
          description:
            '비대면 진료, 약 처방, 진료비, 건강 관련 궁금증을 1분 만에 해결해보세요. 닥터나우 FAQ로 쉽고 빠르게 확인할 수 있어요.',
          url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.FAQ.LIST.path}${addQueryString({
            page: viewModel.queries.page,
          })}`,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_MAGAZINE_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.FAQ.LIST.path}${addQueryString({
          page: viewModel.queries.page,
        })}`}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: '비대면 진료 FAQ, 약 배송, 진료비, 탈모 처방, 여드름 약',
          },
        ]}
      />
      <FaqTemplate client={client} viewModel={viewModel} />
    </>
  );
};

FaqPage.layout = DefaultLayout;
export default FaqPage;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const page = (ctx.query?.page as string) ?? '1';
  const FAQ_SIZE = 20;
  const queryClient = new QueryClient();

  const variables = {
    size: FAQ_SIZE,
    page: Number(page),
    categoryIds: [],
    contentTypes: [MagazineType.FAQ],
  };

  await Promise.all([
    useSearchMagazines.prefetchItemsQuery(queryClient, variables),
    useSearchMagazines.prefetchTotalCountQuery(queryClient, variables),
  ]);

  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
};
